import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, CardHeader, Spinner, Alert } from "reactstrap";
import ReactApexChart from "react-apexcharts";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const cardShell = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: "10px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const iconChip = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
  background: toRgba(ORANGE_COLORS.primary, 0.10),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const iconGlyph = {
  color: ORANGE_COLORS.primary,
  fontSize: 16,
};

function groupByFramework(mappings) {
  const frameworkCounts = mappings.reduce((acc, mapping) => {
    acc[mapping.framework] = (acc[mapping.framework] || 0) + 1;
    return acc;
  }, {});
  return [{ data: Object.entries(frameworkCounts).map(([x, y]) => ({ x, y })) }];
}

function groupByMonth(mappings) {
  const counts = mappings.reduce((acc, m) => {
    const month = new Date(m.created_at).toLocaleString("default", { month: "short" });
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, {});

  const order = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  return {
    categories: order,
    values: order.map(m => counts[m] || 0)
  };
}

export default function ComplianceFramework() {
  const [frameworkData, setFrameworkData] = useState([]);
  const [months, setMonths] = useState([]);
  const [monthValues, setMonthValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const authUser = JSON.parse(sessionStorage.getItem("authUser"));
        const headers = {
          "Content-Type": "application/json",
          ...(authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
        };

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        const res = await fetch(
          `${API_BASE_URL}/threats/compliance-mappings?limit=500`, 
          { headers, signal: controller.signal }
        );
        
        clearTimeout(timeoutId);
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        const json = await res.json();
        console.log('Framework Data:', json);

        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          setFrameworkData(groupByFramework(json.data));
          const monthData = groupByMonth(json.data);
          setMonths(monthData.categories);
          setMonthValues(monthData.values);
        } else {
          setError('No compliance data available');
        }
      } catch (err) {
        console.error("Error fetching compliance data:", err);
        if (err.name === 'AbortError') {
          setError('Request timed out');
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const DiffColorTreemap = ({ series }) => {
    if (!series || !series.length || !series[0].data.length) {
      return (
        <div className="text-center py-5">
          <div style={{ ...iconChip, width: 48, height: 48, margin: "0 auto 12px" }}>
            <i className="ri-layout-grid-line" style={iconGlyph}></i>
          </div>
          <p className="text-muted">No framework coverage data available</p>
        </div>
      );
    }

    var options = {
      legend: {
        show: false
      },
      chart: {
        height: 350,
        type: 'treemap',
        toolbar: {
          show: false
        }
      },
      colors: [ORANGE_COLORS.primary, ORANGE_COLORS.dark, ORANGE_COLORS.light, ORANGE_COLORS.darker, ORANGE_COLORS.lighter, ORANGE_COLORS.muted],
      plotOptions: {
        treemap: {
          distributed: true,
          enableShades: false
        }
      }
    };

    return (
      <ReactApexChart 
        dir="ltr"
        className="apex-charts"
        series={series}
        options={options}
        type="treemap"
        height={350}
      />
    );
  };

  const BasicLineCharts = ({ categories, series }) => {
    if (!series || !series.length || !series[0].data.some(val => val > 0)) {
      return (
        <div className="text-center py-5">
          <div style={{ ...iconChip, width: 48, height: 48, margin: "0 auto 12px" }}>
            <i className="ri-line-chart-line" style={iconGlyph}></i>
          </div>
          <p className="text-muted">No violation timeline data available</p>
        </div>
      );
    }

    var options = {
      chart: {
        height: 350,
        type: 'line',
        zoom: {
          enabled: false
        },
        toolbar: {
          show: false
        }
      },
      markers: {
        size: 4,
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        width: 3,
        colors: [ORANGE_COLORS.primary]
      },
      colors: [ORANGE_COLORS.primary],
      xaxis: { 
        categories 
      },
      yaxis: {
        title: {
          text: 'Violations Count',
          style: {
            color: ORANGE_COLORS.dark
          }
        }
      },
      grid: {
        borderColor: ORANGE_COLORS.muted
      }
    };

    return (
      <ReactApexChart 
        dir="ltr"
        options={options}
        series={series}
        type="line"
        height="350"
        className="apex-charts"
      />
    );
  };

  if (loading) {
    return (
      <Row className="g-3 mb-4">
        <Col xl={6}>
          <Card style={{ border: `1px solid ${ORANGE_COLORS.muted}` }}>
            <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
              <Spinner style={{ color: ORANGE_COLORS.primary }} />
            </CardBody>
          </Card>
        </Col>
        <Col xl={6}>
          <Card style={{ border: `1px solid ${ORANGE_COLORS.muted}` }}>
            <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
              <Spinner style={{ color: ORANGE_COLORS.primary }} />
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
  <Row className="g-3 mb-4">
    <Col xl={6}>
      <Card style={cardShell}>
        <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{ ...iconChip, marginRight: 8 }}>
              <i className="ri-layout-grid-line" style={iconGlyph}></i>
            </span>
            Framework Coverage
          </h5>
        </CardHeader>
        <CardBody>
          {error ? (
            <Alert style={{ backgroundColor: ORANGE_COLORS.subtle, color: ORANGE_COLORS.darker, border: `1px solid ${ORANGE_COLORS.muted}` }}>
              <i className="ri-alert-line me-2"></i>
              {error}
            </Alert>
          ) : (
            <DiffColorTreemap series={frameworkData} />
          )}
        </CardBody>
      </Card>
    </Col>

    <Col xl={6}>
      <Card style={cardShell}>
        <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{ ...iconChip, marginRight: 8 }}>
              <i className="ri-line-chart-line" style={iconGlyph}></i>
            </span>
            Compliance Violations Over Time
          </h5>
        </CardHeader>
        <CardBody>
          {error ? (
            <Alert style={{ backgroundColor: ORANGE_COLORS.subtle, color: ORANGE_COLORS.darker, border: `1px solid ${ORANGE_COLORS.muted}` }}>
              <i className="ri-alert-line me-2"></i>
              {error}
            </Alert>
          ) : (
            <BasicLineCharts 
              categories={months}
              series={[{ name: "Violations", data: monthValues }]} 
            />
          )}
        </CardBody>
      </Card>
    </Col>
  </Row>
);
}
